import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import Auth from './pages/Auth';
import Overview from './pages/Overview';
import Tasks from './pages/Tasks';
import Schedule from './pages/Schedule';
import Team from './pages/Team';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import ShiftNotes from './pages/ShiftNotes';
import Census from './pages/Census';
import MyAvailability from './pages/MyAvailability';
import MySchedule from './pages/MySchedule';
import StaffHome from './pages/StaffHome';
import AvailabilityAdmin from './pages/AvailabilityAdmin';
import AvailabilityHeatmap from './pages/AvailabilityHeatmap';
import { isAdmin, clearSession } from './lib/permissions';
import { Toaster, toast } from './components/ui/sonner';
import './App.css';

// ============================================================================
// Global Authorization header for every axios request.
// The backend refactor added `Depends(require_admin)` to many write endpoints
// (Team / Tasks / Shifts / Census / Shift Notes). Most page components don't
// thread an explicit `headers: { Authorization: ... }` per call, so we attach
// the current user's bearer token via a single interceptor here.
// ============================================================================
axios.interceptors.request.use((config) => {
  try {
    const raw = localStorage.getItem('currentUser');
    if (raw) {
      const user = JSON.parse(raw);
      if (user && user.token) {
        config.headers = config.headers || {};
        // Don't clobber an explicit Authorization passed by a caller.
        if (!config.headers.Authorization && !config.headers.authorization) {
          config.headers.Authorization = `Bearer ${user.token}`;
        }
      }
    }
  } catch (_) {
    /* localStorage parse failed — proceed without auth */
  }
  return config;
});

// ============================================================================
// Global response interceptor for auth errors.
//   • 401 → session is invalid/expired. Clear it and bounce to /auth.
//   • 403 → user is logged in but lacks permission. Show a clear toast; do
//           NOT log them out (a staff user is still authenticated, they just
//           can't perform admin actions).
//
// 403 toasts are debounced per 4s window so a burst of failures (e.g., a
// page fetching 5 endpoints in parallel) surfaces as a single message.
//
// 401 handling uses a one-shot redirect guard so concurrent 401s coalesce
// into a single toast + single redirect (rather than racing each other).
// ============================================================================
const _authToastLast = { 403: 0 };
let _redirectScheduled = false;

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401) {
      try { localStorage.removeItem('currentUser'); } catch (_) {}
      if (!_redirectScheduled) {
        _redirectScheduled = true;
        toast.error('Your session has expired. Please log in again.');
        if (typeof window !== 'undefined' && window.location.pathname !== '/auth') {
          // Hard-navigate so React state (currentUser, etc.) resets cleanly.
          // Delay slightly so the toast is readable before the reload.
          setTimeout(() => window.location.replace('/auth'), 1800);
        }
      }
    } else if (status === 403) {
      const now = Date.now();
      if (now - (_authToastLast[403] || 0) >= 4000) {
        _authToastLast[403] = now;
        const detail = error?.response?.data?.detail;
        toast.error(
          (typeof detail === 'string' && detail) ||
          'You do not have permission to perform this action.'
        );
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'light';
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  const [currentUser, setCurrentUser] = useState(() => {
    const saved = localStorage.getItem('currentUser');
    return saved ? JSON.parse(saved) : null;
  });

  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Auto-collapse sidebar on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarCollapsed(true);
      } else {
        setSidebarCollapsed(false);
      }
    };

    // Set initial state
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const handleLogin = (user) => {
    setCurrentUser(user);
  };

  const handleLogout = () => {
    clearSession();
    setCurrentUser(null);
  };

  // Normalized once; all route guards below use this single check.
  const admin = isAdmin(currentUser);
  const adminRoute = (element) => (admin ? element : <Navigate to="/" replace />);

  // If not logged in, show auth page
  if (!currentUser) {
    return (
      <BrowserRouter>
        <Auth onLogin={handleLogin} />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <div className="flex h-screen overflow-hidden bg-background" data-testid="app-container">
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <TopBar 
            theme={theme} 
            onThemeToggle={toggleTheme}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            currentUser={currentUser}
            onLogout={handleLogout}
          />
          <main className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="max-w-[1440px] mx-auto">
              <Routes>
                <Route path="/" element={admin ? <Overview /> : <StaffHome />} />
                <Route path="/tasks" element={adminRoute(<Tasks />)} />
                <Route path="/schedule" element={<Schedule />} />
                <Route path="/team" element={adminRoute(<Team />)} />
                <Route path="/reports" element={adminRoute(<Reports />)} />
                <Route path="/shift-notes" element={adminRoute(<ShiftNotes />)} />
                <Route path="/census" element={<Census />} />
                <Route path="/my-availability" element={<MyAvailability />} />
                <Route path="/my-schedule" element={<MySchedule />} />
                <Route path="/availability-admin" element={adminRoute(<AvailabilityAdmin />)} />
                <Route path="/availability-heatmap" element={adminRoute(<AvailabilityHeatmap />)} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/settings" element={adminRoute(<Settings />)} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </main>
        </div>
        <Toaster position="top-right" richColors />
      </div>
    </BrowserRouter>
  );
}

export default App;
